import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Wifi } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { getStaffLandingPath } from '../lib/staffLanding';
import api from '../lib/api';

/**
 * A branch's dedicated remote-access address — /b/{slug}. Lets the owner or
 * staff sign in to this one branch from anywhere without installing the
 * desktop app. Owners get full access; staff land read-only (enforced
 * server-side in auth.py, not just in the UI).
 */
export default function BranchRemoteLogin() {
  const { slug } = useParams();
  const { user, remoteLogin } = useAuth();
  const navigate = useNavigate();
  const [branch, setBranch] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/public/branches/${slug}`)
      .then((r) => setBranch(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await remoteLogin(slug, form.email, form.password);
      toast.success(userData.remote_readonly
        ? `Signed in to ${userData.remote_branch_name} — read-only from here`
        : `Signed in to ${userData.remote_branch_name} — full access`);
      navigate(getStaffLandingPath(userData), { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  if (user) return <Navigate to={getStaffLandingPath(user)} replace />;

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="branch-remote-not-found">
        <p className="text-sm text-muted-foreground">This remote-access link isn't recognised. Ask the branch owner for a fresh one.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="branch-remote-login-page">
      <div className="w-full max-w-md">
        <Card className="border-border shadow-sm">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
              <Wifi className="h-3.5 w-3.5" /> Remote branch access
            </div>
            <CardTitle className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {branch ? branch.branch_name : <Loader2 className="h-5 w-5 animate-spin" />}
            </CardTitle>
            <CardDescription>
              {branch ? `Sign in to ${branch.pharmacy_name} from anywhere.` : 'Loading this branch…'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="remote-email">Email</Label>
                <Input id="remote-email" type="email" required
                  value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  data-testid="branch-remote-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="remote-password">Password</Label>
                <Input id="remote-password" type="password" required
                  value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  data-testid="branch-remote-password-input" />
              </div>
              <Button type="submit" className="w-full h-10" disabled={loading || !branch} data-testid="branch-remote-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Sign in to this branch
              </Button>
            </form>
            <p className="text-xs text-center text-muted-foreground mt-5">
              Owners get full access here, same as on-site. Staff get read-only — look things up,
              no changes — until they're back on the branch's own desktop app.
            </p>
            <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>Only accounts that belong to this branch's pharmacy can sign in here</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
